import { EventDto, IPagedCollection } from '@sr/dto';
import { Box, debounce, LinearProgress, Stack } from '@mui/material';
import EventItem from 'components/Companies/Feed/event-item';
import { ReactNode, useCallback, useEffect } from 'react';

type EventsListSharedProps = {
  collection: IPagedCollection<EventDto>;
  isLoading: boolean;
  loadMore?: () => void;
};

type EventsListProps = EventsListSharedProps & {
  showCompany: boolean;
};

type TaskEventsListProps = EventsListProps & {
  checkedItems?: number[];
  handleCheckChange: (id: number, checked: boolean) => void;
};

type EventsListBaseProps = EventsListSharedProps & {
  renderEvent: (event: EventDto, index: number) => ReactNode;
};

const COMPONENT_ID = 'events_list';
const SCROLL_DEBOUNCE_DELAY = 1000;

const EventsListBase = ({ collection, isLoading, loadMore, renderEvent }: EventsListBaseProps) => {
  const handleScroll = useCallback(() => {
    const eventsList = document.querySelector(`#${COMPONENT_ID}`);
    if (!eventsList) return;
    if (!isLoading && eventsList.scrollTop * 1.1 + eventsList.clientHeight >= eventsList.scrollHeight) {
      if (loadMore) {
        loadMore();
      }
    }
  }, [isLoading, loadMore]);

  const debouncedHandleScroll = debounce(() => {
    handleScroll();
  }, SCROLL_DEBOUNCE_DELAY);

  useEffect(() => {
    return () => {
      debouncedHandleScroll.clear();
    };
  }, [debouncedHandleScroll]);

  useEffect(() => {
    const eventsList = document.querySelector(`#${COMPONENT_ID}`);
    if (eventsList && collection.items.length < collection.totalItemsCount) {
      eventsList.addEventListener('scroll', debouncedHandleScroll);
    }
    return () => {
      const eventsList = document.querySelector(`#${COMPONENT_ID}`);
      if (eventsList) eventsList.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [collection, debouncedHandleScroll]);

  return (
    <>
      {!isLoading && collection.items.length === 0 && (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <p>Нет событий </p>
        </Box>
      )}
      <Box sx={{ overflowY: 'auto', height: '70vh' }} id={COMPONENT_ID}>
        <Stack direction="column" spacing={1} sx={{ mt: 2 }}>
          {collection.items.map((event, idx) => renderEvent(event, idx))}
        </Stack>
        {isLoading && <LinearProgress />}
        {!isLoading && collection.items.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {
              <p>
                Показано {collection.items.length} событий из {collection.totalItemsCount}
              </p>
            }
          </Box>
        )}
      </Box>
    </>
  );
};

export const EventsList = ({ collection, isLoading, loadMore, showCompany }: EventsListProps) => (
  <EventsListBase collection={collection} isLoading={isLoading} loadMore={loadMore} renderEvent={(event) => <EventItem showCompany={showCompany} key={event.id} item={event} />} />
);

export const TaskEventsList = ({ collection, isLoading, loadMore, showCompany, checkedItems = [], handleCheckChange }: TaskEventsListProps) => (
  <EventsListBase
    collection={collection}
    isLoading={isLoading}
    loadMore={loadMore}
    renderEvent={(event) => (
      <EventItem
        showCompany={showCompany}
        key={event.id}
        item={event}
        checked={checkedItems.includes(event.id)}
        onCheckChange={(checked) => handleCheckChange(event.id, checked)}
      />
    )}
  />
);
